import React, { useEffect, useState } from "react";
import { Modal } from "reactstrap";
import moment from "moment";
import { useCurrencyFormatter } from "../Hooks/useCurrencyFormatter";
import { theme } from "../../pages/Contract/ContractDetail/theme";
import { PAYMENT_TYPE_LABELS } from "../../common/paymentTypes";

// Codes the backend raises when the series/number already belongs to another
// invoice or the payment is already linked — everything else (SmartBill
// account/lookup failures, phase resolution, ...) is a different kind of
// problem and gets a plain error banner instead of the "duplicate" state.
const DUPLICATE_ERROR_CODES = ["INVOICE_ALREADY_EXISTS", "PAYMENT_ALREADY_LINKED"];

const labelStyle = {
  display: "block",
  fontSize: 12,
  fontWeight: 600,
  color: theme.textMuted,
  marginBottom: 5,
};

const inputStyle = {
  width: "100%",
  fontSize: 13.5,
  padding: "8px 10px",
  borderRadius: 9,
  border: "1px solid #dcdce3",
  outline: "none",
  color: theme.text,
  background: theme.surface,
};

const cancelBtnStyle = {
  fontSize: 13.5,
  fontWeight: 600,
  padding: "8px 16px",
  borderRadius: 9,
  border: `1px solid ${theme.border}`,
  background: theme.surface,
  color: theme.text,
  cursor: "pointer",
};

const primaryBtnStyle = {
  fontSize: 13.5,
  fontWeight: 600,
  padding: "8px 16px",
  borderRadius: 9,
  border: "none",
  background: theme.accent,
  color: "#fff",
};

const LinkInvoiceModal = ({
  isOpen,
  toggle,
  payment,
  onLinkInvoice,
  onDismissError,
  loading = false,
  serverError = null,
  errorCode = null,
}) => {
  const { formatCurrency } = useCurrencyFormatter();
  const [seriesName, setSeriesName] = useState("");
  const [seriesNumber, setSeriesNumber] = useState("");

  useEffect(() => {
    if (isOpen) {
      setSeriesName("");
      setSeriesNumber("");
    }
  }, [isOpen, payment]);

  if (!payment) return null;

  const isDuplicate = DUPLICATE_ERROR_CODES.includes(errorCode);
  const hasOtherError = !!serverError && !isDuplicate;
  const isComplete = !!seriesName.trim() && !!seriesNumber.trim();
  const canSubmit = isComplete && !isDuplicate && !loading;

  const dismissErrorIfAny = () => {
    if (serverError && onDismissError) onDismissError();
  };

  const handleSeriesNameChange = (e) => {
    setSeriesName(e.target.value);
    dismissErrorIfAny();
  };

  const handleSeriesNumberChange = (e) => {
    setSeriesNumber(e.target.value);
    dismissErrorIfAny();
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    onLinkInvoice &&
      onLinkInvoice({
        series_name: seriesName.trim(),
        series_number: seriesNumber.trim(),
        payment_id: payment.id,
      });
  };

  const paymentName = PAYMENT_TYPE_LABELS[payment.type] || payment.type;
  const dueText = payment.booking_date ? moment(payment.booking_date).format("D MMM YYYY") : null;
  const metaParts = [
    dueText ? `Due ${dueText}` : null,
    payment.share != null ? `${payment.share}% of contract` : null,
  ].filter(Boolean);

  const reference = `${seriesName || "···"}/${seriesNumber || "···"}`;

  let previewState = "neutral";
  if (isDuplicate) previewState = "duplicate";
  else if (isComplete) previewState = "valid";

  const previewStyles = {
    neutral: {
      bg: theme.neutralBadgeBg,
      border: theme.border,
      msgColor: theme.textMuted,
      msg: "Enter series and number",
    },
    valid: {
      bg: theme.accentSoftBg,
      border: theme.accentSoftBorder,
      msgColor: theme.success,
      msg: "Ready to link",
    },
    duplicate: {
      bg: theme.dangerBg,
      border: theme.dangerBadgeBg,
      msgColor: theme.dangerText,
      msg: "Already linked to another payment",
    },
  }[previewState];

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      contentClassName="border-0 bg-transparent"
      style={{ maxWidth: 520 }}
    >
      <div
        style={{
          background: theme.surface,
          borderRadius: 16,
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            padding: "20px 24px 16px",
            borderBottom: `1px solid ${theme.border}`,
            flexShrink: 0,
          }}
        >
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: theme.text }}>
              Link an existing invoice
            </div>
            <div style={{ fontSize: 13, color: theme.textMuted, marginTop: 4 }}>
              Enter the series of an invoice issued outside the app
            </div>
          </div>
          <button
            type="button"
            onClick={toggle}
            aria-label="Close"
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              border: `1px solid ${theme.border}`,
              background: theme.surface,
              color: theme.textMuted,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              flexShrink: 0,
              marginLeft: 12,
            }}
          >
            <i className="ri-close-line" />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 24, overflowY: "auto" }}>
          {hasOtherError && (
            <div
              style={{
                background: theme.dangerBg,
                border: `1px solid ${theme.dangerBadgeBg}`,
                color: theme.dangerText,
                borderRadius: 10,
                padding: "10px 12px",
                fontSize: 13,
                marginBottom: 16,
              }}
            >
              {serverError}
            </div>
          )}

          {/* Payment summary strip */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              background: theme.surfaceSubtle,
              borderRadius: 12,
              padding: "14px 16px",
              marginBottom: 20,
            }}
          >
            <div>
              <div style={{ fontSize: 14.5, fontWeight: 650, color: theme.text }}>{paymentName}</div>
              {metaParts.length > 0 && (
                <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 3 }}>
                  {metaParts.join(" · ")}
                </div>
              )}
            </div>
            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 650,
                  color: theme.text,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {formatCurrency(parseFloat(payment.amount || 0), "EUR")}
              </div>
              {payment.amount_ron != null && (
                <div
                  style={{
                    fontSize: 12,
                    color: theme.textMuted,
                    marginTop: 2,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatCurrency(payment.amount_ron, "RON")}
                </div>
              )}
            </div>
          </div>

          {/* Series fields */}
          <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
            <div style={{ flex: 1.4 }}>
              <label style={labelStyle}>Series name</label>
              <input
                type="text"
                value={seriesName}
                onChange={handleSeriesNameChange}
                placeholder="e.g. FRB"
                autoComplete="off"
                style={{ ...inputStyle, }}
                onFocus={(e) => (e.target.style.borderColor = theme.accent)}
                onBlur={(e) => (e.target.style.borderColor = "#dcdce3")}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Series number</label>
              <input
                type="text"
                value={seriesNumber}
                onChange={handleSeriesNumberChange}
                placeholder="e.g. 0142"
                autoComplete="off"
                style={{ ...inputStyle, fontVariantNumeric: "tabular-nums" }}
                onFocus={(e) => (e.target.style.borderColor = theme.accent)}
                onBlur={(e) => (e.target.style.borderColor = "#dcdce3")}
              />
            </div>
          </div>

          {/* Live preview */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: previewStyles.bg,
              border: `1px solid ${previewStyles.border}`,
              borderRadius: 10,
              padding: "10px 14px",
              gap: 12,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, color: theme.textMuted }}>Will be linked as</div>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 650,
                  color: isComplete ? theme.text : theme.textMuted,
                  marginTop: 2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {reference}
              </div>
            </div>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: previewStyles.msgColor, textAlign: "right" }}>
              {previewStyles.msg}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            padding: "16px 24px",
            background: theme.surfaceSubtle,
            borderTop: `1px solid ${theme.border}`,
            flexShrink: 0,
          }}
        >
          <button type="button" onClick={toggle} disabled={loading} style={cancelBtnStyle}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              ...primaryBtnStyle,
              opacity: canSubmit ? 1 : 0.5,
              cursor: canSubmit ? "pointer" : "not-allowed",
            }}
          >
            {loading ? "Linking…" : "Link invoice"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default LinkInvoiceModal;
